import React, { createContext, useContext, useState, useMemo } from 'react'

import genericMRTExampleForHookUp from '../utils/ExampleJSONs/generic-instructional/genericMRTExampleForHookUp.json'

import { useAuthInfo } from '@propelauth/react'

import { getMRTOutline, getOneMRTTemplate, upsertExampleToSection, updateGenericMRT } from '../utils/utils'
import { v4 as uuidv4 } from 'uuid'

const InstructionalMRTContext = createContext()

export const useInstructionalMRT = () => {
    const context = useContext(InstructionalMRTContext)
    if (!context) {
        throw new Error('useInstructionalMRT must be used within an InstructionalMRTProvider')
    }
    return context
}

export const InstructionalMRTProvider = ({ children }) => {
    const [instructionalMRT, setInstructionalMRT] = useState([])
    const [selectedSection, setSelectedSection] = useState({})
    const [originalSection, setOriginalSection] = useState({})
    const [document, setDocument] = useState({})
    const [sessionChanges, setSessionChanges] = useState({}) // Track all changes across session
    const [examples, setExamples] = useState({})
    
    const authInfo = useAuthInfo()
    const org = authInfo.orgHelper.getOrgs()[0].orgId

    const cancelChanges = () => {
        setSelectedSection({...originalSection})
        setInstructionalMRT(prev => {
            return prev.map(section => {
                if (section.section_id === originalSection.section_id) {
                    return { ...section, ...originalSection }
                }
                return section
            })
        })
    }

    const upsertExampleTextToSection = async (
        orgAccessToken
    ) => {
        const section_id = selectedSection.section_id
        const example = [...examples]

        console.log("section_id: ", section_id)
        console.log("example: ", example)

        const jsonPayload = {
            example: example,
            section_id: section_id
        }

        const response = await upsertExampleToSection(orgAccessToken, jsonPayload)
        console.log("response: ", response)

        return response
    }

    const changeExampleText = async (
        orgAccessToken,
        section_id,
        example
    ) => {
        const jsonPayload = {
            example: example,
            section_id: section_id
        }

        const response = await upsertExampleToSection(orgAccessToken, jsonPayload)
        console.log("response: ", response)

        return response
    }


    // Load the appropriate instructional MRT based on template ID
    const loadInstructionalMRT = async (
        setLoading, 
        setError, 
        templateId='', 
        production=true
    ) => {
        let mrt

        
        const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(org)
        const mrt_template = await getOneMRTTemplate(templateId, orgAccessToken)
        
        mrt = mrt_template

        const rawJSON = await getMRTOutline(
            setLoading,
            setError,
            org,
            mrt
        )

        const jsonWithUUID = rawJSON.sections.map(section => {
            let examplesWithUUID = []
            let rulesWithUUID = []

            if ((section.examples || []).length > 0) {
                section.examples.map(example => {
                    examplesWithUUID.push({
                        id: uuidv4(),
                        ...example,
                    })
                })
            }

            if ((section.rules || []).length > 0) {
                section.rules.map(rule => {
                    rulesWithUUID.push({
                        id: uuidv4(),
                        ...rule,
                    })
                })
            }

            return {
                section_id: section.section_id || uuidv4(),
                examples: examplesWithUUID,
                rules: rulesWithUUID,
                ...section,
            }
        })

        const documentWithUUID = {
            ...rawJSON,
            sections: jsonWithUUID
        }

        console.log("InstructionalMRT", [...jsonWithUUID])

        setInstructionalMRT([...jsonWithUUID])
        setDocument(documentWithUUID)
        setSessionChanges({}) // Reset session changes on load
        
        // Auto-select the first section if available
        if (jsonWithUUID.length > 0) {
            setSelectedSection({ ...jsonWithUUID[0] })
            setOriginalSection({ ...jsonWithUUID[0] })
        }

        return documentWithUUID
    }

    // Select a section for editing
    const selectSectionForEdit = (section) => {
        // Apply any existing session changes to the section before selecting
        const sectionWithChanges = sessionChanges[section.section_id] 
            ? { ...section, ...sessionChanges[section.section_id] }
            : section
            
        setSelectedSection({ ...sectionWithChanges })
        setOriginalSection({ ...section }) // Keep original without session changes
        console.log("selectedSection: ", selectedSection)
    }


    // Update Examples
    const updateExamples = (value, section) => {
        const sec_id = section.section_id

        const example = { 
            ...examples, 
            text: value 
        }

        setExamples({
            example: example,
            section_id: String(sec_id),
        })
    }

    // Update the selected section and track in session changes
    const updateSelectedSection = (updates) => {
        setSelectedSection(prev => {
            // Only update if values actually changed
            const hasActualChanges = Object.keys(updates).some(key => {
                return JSON.stringify(prev[key]) !== JSON.stringify(updates[key])
            })
            
            if (!hasActualChanges) {
                return prev // Skip update if nothing changed
            }
            
            const newSection = { ...prev, ...updates }
            
            // Track this change in session changes
            const sectionId = prev.section_id
            if (sectionId) {
                setSessionChanges(prevChanges => ({
                    ...prevChanges,
                    [sectionId]: {
                        ...prevChanges[sectionId],
                        ...updates
                    }
                }))
            }
            
            setInstructionalMRT(prevMRT => {
                return prevMRT.map(section => {
                    if (section.section_id === sectionId) {
                        return { ...section, ...updates }
                    }
                    return section
                })
            })
            
            return newSection
        })
    }

    const updateDocument = (updates) => {
        console.log("InstructionalMRT", instructionalMRT)
        setDocument(prev => ({ 
            ...prev, 
            sections: prev.sections.map(section => {
                if ((section.section_id || section.id) === (updates.section_id || updates.id)) {
                    return { 
                        ...section, 
                        ...updates,
                        rules: section.rules.map(rule => {
                            if (rule.id === updates.id) {
                                return { ...rule, ...updates }
                            }
                            return rule
                        })
                    }
                }
                return section
            })
        }))
    }

    const deleteSection = async (section) => {
        // Use the correct identifier - sections might use section_id instead of id
        const sectionId = section.section_id || section.id

        const updatedMRT = instructionalMRT.filter(section => (section.section_id || section.id) !== sectionId)
        const updatedDocument = document.sections.filter(section => (section.section_id || section.id) !== sectionId)

        const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(org)
        const jsonPayload = {
            generic_mrt_id: document.template_id,
            sections: updatedMRT
        }

        const response = await updateGenericMRT(orgAccessToken, jsonPayload)
        console.log("response: ", response)

        setInstructionalMRT(updatedMRT)
        setDocument(prev => {
            return {
                ...prev,
                sections: updatedDocument
            }
        })
        
        // Remove from session changes
        setSessionChanges(prev => {
            const newChanges = { ...prev }
            delete newChanges[sectionId]
            return newChanges
        })
    }
    

    const addSectionBefore = (section) => {
        const newSection = {
            id: uuidv4(),
            title: section.section_name,
            section_number: section.instructions,
            contains_content: section.contains_content,
            fixed_elements: section.fixed_elements,
        }

        const sectionsBefore = instructionalMRT.slice(0, instructionalMRT.indexOf(section))
        const sectionsAfter = instructionalMRT.slice(instructionalMRT.indexOf(section) + 1)

        setInstructionalMRT([...sectionsBefore, newSection, ...sectionsAfter])
    }

    const addSectionAfter = (section) => {
        const newSection = {
            id: uuidv4(),
            section_name: "",
            instructions: "",
            
            rules: [],
        }

        const sectionsBefore = instructionalMRT.slice(0, instructionalMRT.indexOf(section))
        const sectionsAfter = instructionalMRT.slice(instructionalMRT.indexOf(section) + 1)

        setInstructionalMRT([...sectionsBefore, newSection, ...sectionsAfter])
    }

    // Save the current selected section changes without clearing
    const saveCurrentSectionChanges = () => {
        if (!selectedSection.section_id) return

        // Update the instructionalMRT with current section changes
        const updatedMRT = instructionalMRT.map(section => {
            if (section.section_id === selectedSection.section_id) {
                return { ...selectedSection }
            }
            return section
        })

        setInstructionalMRT(updatedMRT)
        // Don't clear selected section or session changes
    }

    // Save all session changes to backend
    const saveAllChanges = async () => {
        // First save current selected section if any
        if (selectedSection.section_id) {
            saveCurrentSectionChanges()
        }

        // Apply all session changes to instructionalMRT
        const updatedMRT = instructionalMRT.map(section => {
            const changes = sessionChanges[section.section_id]
            if (changes) {
                return { ...section, ...changes }
            }
            return section
        })

        const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(org)
        const jsonPayload = {
            section_id: selectedSection.section_id,
            example: selectedSection.examples
        }

        const response = await upsertExampleToSection(orgAccessToken, jsonPayload)

        console.log("response: ", response)

        setInstructionalMRT(updatedMRT)
        setSessionChanges({}) // Clear session changes after successful save
        clearSelectedSection()
    }

    // Save changes to the instructional MRT (original method for backward compatibility)
    const saveChanges = async () => {
        console.log("selectedSection: ", selectedSection)
        if (!selectedSection.section_id && !selectedSection.title) return

        // Only send the updated selected section, not all sections
        const payload = {
            generic_mrt_id: document.template_id,
            sections: [{ ...selectedSection }]
        }

        const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(org)

        const response = await updateGenericMRT(orgAccessToken, payload)

        console.log("response: ", response)

        // Update local state immediately
        // clearSelectedSection()
        setOriginalSection(selectedSection)
    }

    // Clear the selected section (discard changes)
    const clearSelectedSection = () => {
        setSelectedSection({})
        setOriginalSection({})
    }

    // Check if there are unsaved changes
    const checkForChanges = () => {
        return JSON.stringify(selectedSection) !== JSON.stringify(originalSection)
    }

    // Memoize expensive calculations
    const hasSessionChangesValue = useMemo(() => Object.keys(sessionChanges).length > 0, [sessionChanges])
    const hasChanges = useMemo(() => JSON.stringify(selectedSection) !== JSON.stringify(originalSection), [selectedSection, originalSection])
    
    
    // Memoize the context value to prevent unnecessary re-renders
    const contextValue = useMemo(() => ({
        instructionalMRT,
        setInstructionalMRT,
        selectedSection,
        setSelectedSection,
        originalSection,
        document,
        sessionChanges,
        hasSessionChanges: hasSessionChangesValue,
        hasChanges,
        examples,
        // Functions
        loadInstructionalMRT,
        selectSectionForEdit,
        updateSelectedSection,
        updateDocument,
        updateExamples,
        upsertExampleTextToSection,
        changeExampleText,
        deleteSection,
        addSectionBefore,
        addSectionAfter,
        clearSelectedSection,
        saveChanges,
        saveCurrentSectionChanges,
        saveAllChanges,
        upsertExampleToSection,
        cancelChanges
    }), [
        instructionalMRT, 
        selectedSection, 
        originalSection, 
        document, 
        sessionChanges, 
        examples,
        hasSessionChangesValue,
        hasChanges
    ])

    return (
        <InstructionalMRTContext.Provider value={contextValue}>
            {children}
        </InstructionalMRTContext.Provider>
    )
} 